/**
 * Open/close state and viewport positioning for the suggestion menu, including
 * dismissal on Escape, outside click and resize.
 *
 * Opens either at a cursor point (right-click) or anchored over a word (tap), in
 * which case it centres on the word and flips below when there is no room above.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;
const ARROW_INSET = 14;

const clamp = (value, min, max) => Math.max(min, Math.min(value, Math.max(min, max)));

export default function useSuggestionMenu() {
  const menuRef = useRef(null);
  const [menu, setMenu] = useState(null);

  const close = useCallback(() => setMenu(null), []);

  const openAtPoint = useCallback(
    (x, y, range) => setMenu({ placement: "point", x, y, range }),
    []
  );

  const openOverWord = useCallback(
    (rect, range) => setMenu({ placement: "word", rect, range }),
    []
  );

  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!menu || !node) return;

    const { width, height } = node.getBoundingClientRect();
    const maxLeft = window.innerWidth - width - VIEWPORT_MARGIN;
    const maxTop = window.innerHeight - height - VIEWPORT_MARGIN;

    if (menu.placement === "point") {
      node.style.left = `${clamp(menu.x, VIEWPORT_MARGIN, maxLeft)}px`;
      node.style.top = `${clamp(menu.y, VIEWPORT_MARGIN, maxTop)}px`;
      node.removeAttribute("data-flipped");
      return;
    }

    const { rect } = menu;
    const above = rect.top - height - ANCHOR_GAP;
    const flipped = above < VIEWPORT_MARGIN;

    const left = clamp(rect.left + rect.width / 2 - width / 2, VIEWPORT_MARGIN, maxLeft);
    const top = clamp(flipped ? rect.bottom + ANCHOR_GAP : above, VIEWPORT_MARGIN, maxTop);

    node.style.left = `${left}px`;
    node.style.top = `${top}px`;
    node.dataset.flipped = String(flipped);
    node.style.setProperty(
      "--arrow-left",
      `${clamp(rect.left + rect.width / 2 - left, ARROW_INSET, width - ARROW_INSET)}px`
    );
  }, [menu]);

  useEffect(() => {
    if (!menu) return undefined;

    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };

    const frame = requestAnimationFrame(() => {
      document.addEventListener("mousedown", onPointerDown, true);
      document.addEventListener("keydown", onKeyDown, true);
      window.addEventListener("resize", close);
    });

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", close);
    };
  }, [menu, close]);

  return { menu, menuRef, openAtPoint, openOverWord, close };
}
